import { FileText, Database, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { GridReviewMap } from "@/lib/extractionReview";
import type { Document } from "@/types/models";
import type { SectionSources } from "./SortableRow";

interface SectionSourcesSummaryProps {
  documents: Document[];
  extractionPoints: { id: string; name: string; parent_id?: string | null }[];
  gridReview: GridReviewMap;
  sectionSources: SectionSources;
  onOpenSources: () => void;
  className?: string;
}

export const SectionSourcesSummary = ({
  documents,
  extractionPoints,
  gridReview,
  sectionSources,
  onOpenSources,
  className,
}: SectionSourcesSummaryProps) => {
  const selectedDocs = documents.filter((d) => sectionSources.documentIds.includes(d.id));
  const selectedEps = extractionPoints.filter((ep) => sectionSources.extractionPointIds.includes(ep.id));
  const totalCount = selectedDocs.length + selectedEps.length;

  // Saved selections survive a grid reverting to pending review, so flag them rather than drop them.
  const pendingCount = selectedEps.filter(
    (ep) => !gridReview.get(ep.parent_id ?? ep.id)?.isReviewed
  ).length;

  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      {selectedDocs.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Publications:</span>
          {selectedDocs.map((d) => (
            <span key={d.id} className="flex items-center gap-1.5 text-xs text-blue-600 truncate">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="truncate">{d.title}</span>
            </span>
          ))}
        </div>
      )}
      {selectedEps.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Extraction Grids:</span>
          {selectedEps.map((ep) => (
            <span key={ep.id} className="flex items-center gap-1.5 text-xs text-green-700 truncate">
              <Database className="w-3 h-3 shrink-0" />
              <span className="truncate">{ep.name}</span>
            </span>
          ))}
        </div>
      )}
      {pendingCount > 0 && (
        <span className="flex items-center gap-1.5 text-xs text-amber-700">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          <span>{pendingCount} source{pendingCount === 1 ? "" : "s"} pending QA</span>
        </span>
      )}
      <Button
        size="sm"
        variant="brand"
        appearance={totalCount > 0 ? "outlined" : "solid"}
        className="w-full"
        onClick={onOpenSources}
      >
        <Plus className="w-3 h-3 mr-1" />
        {totalCount > 0 ? `${totalCount} Source${totalCount !== 1 ? "s" : ""} Selected` : "Select Sources"}
      </Button>
    </div>
  );
};
